/* @jsx h */
import { defineComponent, ref, h } from "vue";
import { Order } from "@lemmonhq/types";
import { api } from "@app/data/api";
import { useToast } from "@app/vue/providers/Toast";
import { getOrderDisplayName } from "@lemmonhq/orders";
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";

interface RejectOrderModalProps {
  onClose: () => void;
  order: Order;
  onConfirm: (order: Order) => void;
}

export const RejectOrderModal = defineComponent<RejectOrderModalProps>({
  name: "RejectOrderModal",
  props: ["onClose", "onConfirm", "order"],
  setup(props) {
    const { t } = useLocalization();
    const reason = ref("");
    const loading = ref(false);
    const toast = useToast();

    const handleRejectOrder = async () => {
      loading.value = true;
      try {
        const { order } = await api.rejectOrder(props.order.id);
        toast.showToast(t("orders.reject.success"), {
          type: "info",
          autoHide: 3000,
          icon: "cancel",
        });
        props.onConfirm(order);
        props.onClose();
      } catch (error) {
        console.error("Failed to reject order:", error);
        toast.showToast(t("orders.reject.failed"), { type: "error" });
      } finally {
        loading.value = false;
      }
    };

    return () => (
      <LemmonModal
        title={t("orders.reject.title")}
        onClose={props.onClose}
        renderButtons={() => (
          <div class="p-4 border-t ">
            <div class="flex justify-between gap-2">
              <button
                onClick={props.onClose}
                class="btn btn-neutral"
                type="button"
              >
                {t("orders.reject.cancel")}
              </button>
              <button
                onClick={handleRejectOrder}
                class="btn btn-error"
                type="submit"
                disabled={loading.value}
              >
                {loading.value ? (
                  <span class="loading loading-spinner loading-sm"></span>
                ) : (
                  t("orders.reject.confirm")
                )}
              </button>
            </div>
          </div>
        )}
      >
        <div class="p-6 space-y-4">
          <div class="">
            <h3 class="text-lg text-accent font-semibold mb-2">
              {getOrderDisplayName(props.order)}
            </h3>
            <p class=" text-sm">
              {t("orders.accept.table_label")}{" "}
              {props.order.table?.name || "N/A"} |{" "}
              {t("orders.accept.amount_label")} {props.order.amount?.toFixed(2)}
            </p>
          </div>

          <div class="alert bg-base-200">
            <span class="material-symbols-outlined">warning</span>
            <span>{t("orders.reject.warning")}</span>
          </div>

          <div>
            <label class="block text-sm font-medium mb-2">
              {t("orders.reject.reason_label")}
            </label>
            <textarea
              v-model={reason.value}
              class="textarea textarea-bordered w-full"
              rows={3}
              placeholder={t("orders.reject.reason_placeholder")}
            />
          </div>
        </div>
      </LemmonModal>
    );
  },
});
